import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { practiceTopics } from '../data/awsClfGuideData';
import './AWSCloudFundamentalsCourse.css';

const LETTERS = 'ABCD';

const AWSCLFC02PracticePage: React.FC = () => {
  const [topic, setTopic] = useState(-1);
  const [answers, setAnswers] = useState<Record<string, number>>({});

  const total = useMemo(() => practiceTopics.reduce((n, g) => n + g.q.length, 0), []);
  const answered = Object.keys(answers).length;
  const correct = useMemo(
    () =>
      Object.entries(answers).filter(([k, v]) => {
        const [g, q] = k.split('-').map(Number);
        return practiceTopics[g].q[q].a === v;
      }).length,
    [answers]
  );

  let qNumber = 0;

  return (
    <Layout>
      <div className="page-hero">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 6%' }}>
          <div style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '12px' }}>
            <Link to="/programs" style={{ color: 'var(--blue)' }}>Programs</Link> /{' '}
            <Link to="/aws-ccp" style={{ color: 'var(--blue)' }}>AWS Cloud Practitioner</Link> / Practice
          </div>
          <h1>CLF-C02 Practice Questions</h1>
          <p>Practice questions grouped by topic, with instant right-or-wrong feedback and a short reason for each answer.</p>
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <span className="section-tag">{total} questions</span>
            <span className="section-tag">{practiceTopics.length} topics</span>
            <span className="section-tag orange">Instant feedback</span>
          </div>
        </div>
      </div>

      <div className="cf section" style={{ paddingTop: '20px' }}>
        <div className="cf-body">
          <div className="cf-chips">
            <button className={topic === -1 ? 'on' : ''} onClick={() => setTopic(-1)}>All ({total})</button>
            {practiceTopics.map((g, i) => (
              <button key={g.t} className={topic === i ? 'on' : ''} onClick={() => setTopic(i)}>
                {g.i} {g.t} ({g.q.length})
              </button>
            ))}
          </div>

          <div className="cf-score" style={{ top: '70px' }}>
            <span>Score: {correct} / {answered} answered (of {total})</span>
            <button onClick={() => setAnswers({})}>Reset</button>
          </div>

          {practiceTopics.map((g, gi) => {
            const start = qNumber;
            qNumber += g.q.length;
            if (topic !== -1 && topic !== gi) return null;
            return (
              <section className="les" key={g.t}>
                <div className="sh">
                  <i>{g.i}</i>
                  <div>
                    <h2>{g.t}</h2>
                    <small>{g.s} · {g.q.length} questions</small>
                  </div>
                </div>
                {g.q.map((q, qi) => {
                  const key = `${gi}-${qi}`;
                  const picked = answers[key];
                  const done = picked !== undefined;
                  return (
                    <div className="cf-q" key={key}>
                      <h3><span>Q{start + qi + 1}.</span>{q.q}</h3>
                      {q.o.map((o, oi) => (
                        <button
                          key={oi}
                          className={`cf-opt${done && oi === q.a ? ' ok' : ''}${done && oi === picked && oi !== q.a ? ' no' : ''}`}
                          disabled={done}
                          onClick={() => setAnswers((a) => ({ ...a, [key]: oi }))}
                        >
                          {LETTERS[oi]}. {o}
                        </button>
                      ))}
                      {done && (
                        <div className="cf-exp">
                          <p className={picked === q.a ? 'cf-res ok' : 'cf-res no'}>
                            {picked === q.a ? '✓ Correct.' : `✗ Not quite. You chose ${LETTERS[picked]}.`}{' '}
                            <b>Answer: {LETTERS[q.a]}. {q.o[q.a]}</b>
                          </p>
                          <h4>Detailed explanation</h4>
                          <p>{q.r}</p>
                          <h4>Why the other options are wrong</h4>
                          <ul className="cf-wrong">
                            {q.w.map((line, li) => {
                              const dot = line.indexOf('. ');
                              return (
                                <li key={li}>
                                  <b>{line.slice(0, dot + 1)}</b> {line.slice(dot + 2)}
                                </li>
                              );
                            })}
                          </ul>
                          {q.e && <p className="cf-fact"><b>Key fact:</b> {q.e}</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            );
          })}

          <div className="cf-end" style={{ marginTop: '30px' }}>
            <h2>Ready for the exam?</h2>
            <p>Score 80% or higher on a full timed practice exam, then book your CLF-C02 exam.</p>
            <div style={{ marginTop: '14px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/aws-clf-c02-guide" className="btn btn-outline-blue" style={{ background: '#fff' }}>Review the study guide</Link>
              <button className="btn btn-orange" onClick={() => { setAnswers({}); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Practise again</button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AWSCLFC02PracticePage;
